import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import ProblemPanel from "./ProblemPanel";
import FlashcardWarmup from "./FlashcardWarmup";
import {
  DEFAULT_HINT_DELAYS_SEC,
} from "../utils/hintDelays";
import { HINT_LEVELS } from "../data/hintResources";
import { useTasks } from "../context/TaskContext";
import {
  computeEligibleHintLevels,
  computeHintCountdowns,
  computeHintElapsedSec,
} from "../utils/hintTiming";
import { AttemptStates, isHintState } from "../fsm/problemAttemptFsm";
import {
  getHintChimeVolume,
  getHintNotificationConfig,
} from "../utils/hintNotifications";
import formatTime from "../utils/formatTime";

const ToDoItem = ({
  id,
  index,
  task,
  complete,
  primaryDuration: initialPrimaryDuration,
  secondaryDuration: initialSecondaryDuration,
  numCycles: initialNumCycles,
  onToggle,
  onDelete,
  handleUpdate,
  tilDone,
  isRunning: isRunningProp,
  onComplete,
  onStart,
  onPause,
  volume,
  order,
  isProblemSolving = false,
  problem,
  problems = [],
  onStartProblem,
  onFinishProblem,
  onCloseReview,
  onRecordHint,
  onRequestHint,
  onUpdateProblem,
  onReportLatexMismatch,
  onAnalyzeAndCreateProblemFromText,
  onAnalyzeAndCreateProblemFromImage,
  onOcrLatexFromImage,
  onCheckAnswerForProblem,
  hintDelaysSec: taskHintDelaysSec,
}) => {
  const {
    logTelemetryEvent,
    telemetryEnabled,
    submitAttemptPlan,
    recordHintCheckResponse,
    answerChat,
    getDueFlashcards,
    reviewFlashcard,
    createFlashcard,
    requestDesmosExpressions,
  } = useTasks();
  const [primaryDuration, setPrimaryDuration] = useState(
    initialPrimaryDuration
  );
  const [secondaryDuration, setSecondaryDuration] = useState(
    initialSecondaryDuration
  );
  const [timeLeft, setTimeLeft] = useState(initialPrimaryDuration);
  const [isRunning, setIsRunning] = useState(!!isRunningProp);
  const [isPrimary, setIsPrimary] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [currentCycle, setCurrentCycle] = useState(0);
  const [numCycles, setNumCycles] = useState(initialNumCycles);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [taskLabel, setTaskLabel] = useState(task);
  const [problemSolving, setProblemSolving] = useState(!!isProblemSolving);
  const [problemFromApi, setProblemFromApi] = useState(null);
  const [isStudyFocused, setIsStudyFocused] = useState(false);
  const [isHistoryOpen, setIsHistoryOpen] = useState(false);
  const [expandedSolved, setExpandedSolved] = useState({});
  const [unlockedHintLevels, setUnlockedHintLevels] = useState([]);
  const [activeProblemHints, setActiveProblemHints] = useState([]);
  const [focusElapsedSec, setFocusElapsedSec] = useState(0);
  const [hintStartFocusSec, setHintStartFocusSec] = useState(null);
  const [hintToast, setHintToast] = useState("");
  const [warmupCards, setWarmupCards] = useState([]);
  const [warmupActive, setWarmupActive] = useState(false);
  const [warmupLoading, setWarmupLoading] = useState(false);
  const [warmupExitCta, setWarmupExitCta] = useState(null);
  const lastProblemIdRef = useRef(null);
  const previousActiveProblemIdRef = useRef(null);
  const pendingHintStartSecRef = useRef(null);
  const queuedPlanRef = useRef(null);
  const lastEligibleHintLevelsRef = useRef([]);
  const pendingEligibleHintLevelsRef = useRef([]);
  const lastHintNotifyAtRef = useRef(0);
  const hintToastTimeoutRef = useRef(null);
  const warmupSessionRef = useRef(null);
  const stepperTimeout = useRef(null);
  const timeLeftRef = useRef(timeLeft);
  const durationsRef = useRef({
    primaryDuration: initialPrimaryDuration,
    secondaryDuration: initialSecondaryDuration,
    numCycles: initialNumCycles,
  });
  const lastBreakLockedRef = useRef(null);
  const timerStateRef = useRef({
    isPrimary: true,
    currentCycle: 0,
    endAt: null,
    isRunning: false,
    elapsedTilDone: 0,
    focusElapsedSec: 0,
    lastTick: Date.now(),
  });

  const timerStorageKey = useRef(`timerState_${id}`);

  const safeLoad = (key, fallback) => {
    try {
      const raw = localStorage.getItem(key);
      if (!raw) return fallback;
      const parsed = JSON.parse(raw);
      return parsed ?? fallback;
    } catch (error) {
      console.error("Failed to load timer state", error);
      return fallback;
    }
  };

  const safeSave = (key, value) => {
    try {
      localStorage.setItem(key, JSON.stringify(value));
    } catch (error) {
      console.error("Failed to save timer state", error);
    }
  };

  const hintDelaysSec = useMemo(() => {
    if (
      Array.isArray(taskHintDelaysSec) &&
      taskHintDelaysSec.length === DEFAULT_HINT_DELAYS_SEC.length &&
      taskHintDelaysSec.every((value) => Number.isFinite(value) && value >= 0)
    ) {
      return taskHintDelaysSec;
    }
    return DEFAULT_HINT_DELAYS_SEC;
  }, [taskHintDelaysSec]);

  const hintUnlockAtSec = useMemo(
    () =>
      hintDelaysSec.reduce((acc, delay, index) => {
        const level = index + 1;
        const prev = acc[level - 1] || 0;
        acc[level] = prev + delay;
        return acc;
      }, {}),
    [hintDelaysSec]
  );
  const hintNotificationConfig = useMemo(() => getHintNotificationConfig(), []);

  const playBell = useCallback(
    (times = 1) => {
      const ring = (remaining) => {
        if (remaining <= 0) return;
        const audio = new Audio("/gong-transition.mp3");
        audio.volume = volume / 100;
        audio.play();
        if (remaining > 1) {
          setTimeout(() => ring(remaining - 1), 1000);
        }
      };
      ring(times);
    },
    [volume]
  );

  const notifyHintEligible = useCallback(
    (level) => {
      const now = Date.now();
      if (
        hintNotificationConfig.cooldownMs > 0 &&
        now - lastHintNotifyAtRef.current < hintNotificationConfig.cooldownMs
      ) {
        return false;
      }
      lastHintNotifyAtRef.current = now;

      if (hintNotificationConfig.toastEnabled) {
        setHintToast(`Hint ${level} ready`);
        if (hintToastTimeoutRef.current) {
          clearTimeout(hintToastTimeoutRef.current);
        }
        hintToastTimeoutRef.current = setTimeout(() => {
          setHintToast("");
        }, hintNotificationConfig.toastDurationMs);
      }

      if (hintNotificationConfig.chimeEnabled) {
        const chimeVolume = getHintChimeVolume(volume);
        if (chimeVolume > 0) {
          const chime = new Audio("/beep.wav");
          chime.volume = chimeVolume;
          chime.play();
        }
      }

      return true;
    },
    [hintNotificationConfig, volume]
  );

  useEffect(() => {
    setTaskLabel(task);
  }, [task]);

  useEffect(() => {
    setProblemSolving(!!isProblemSolving);
    if (!isProblemSolving) {
      setProblemFromApi(null);
      setIsStudyFocused(false);
    }
  }, [isProblemSolving]);

  useEffect(() => {
    if (!isStudyFocused) return undefined;
    const handleKeyDown = (event) => {
      if (event.key === "Escape") {
        setIsStudyFocused(false);
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      document.body.style.overflow = previousOverflow;
    };
  }, [isStudyFocused]);

  useEffect(() => {
    setNumCycles(initialNumCycles);
  }, [initialNumCycles]);

  useEffect(() => {
    setPrimaryDuration(initialPrimaryDuration);
    setSecondaryDuration(initialSecondaryDuration);
  }, [initialPrimaryDuration, initialSecondaryDuration]);

  useEffect(() => {
    durationsRef.current = {
      primaryDuration,
      secondaryDuration,
      numCycles,
    };
  }, [primaryDuration, secondaryDuration, numCycles]);

  // Load saved timer state
  useEffect(() => {
    const loaded =
      safeLoad(timerStorageKey.current, null) || timerStateRef.current;
    timerStateRef.current = {
      ...timerStateRef.current,
      ...loaded,
    };
    setIsRunning(!!loaded.isRunning);
    setIsPrimary(!!loaded.isPrimary);
    setCurrentCycle(loaded.currentCycle || 0);
    setElapsedTime(loaded.elapsedTilDone || 0);
    setFocusElapsedSec(loaded.focusElapsedSec || 0);
    if (loaded.endAt) {
      const remaining = Math.max(
        0,
        Math.round((loaded.endAt - Date.now()) / 1000)
      );
      setTimeLeft(
        remaining || (loaded.isPrimary ? primaryDuration : secondaryDuration)
      );
      timeLeftRef.current = remaining;
    } else {
      setTimeLeft(primaryDuration);
      timeLeftRef.current = primaryDuration;
    }
  }, [primaryDuration, secondaryDuration]);

  const activeProblemCandidate = problemFromApi || problem;
  const activeProblem =
    activeProblemCandidate &&
    (activeProblemCandidate.status !== "solved" || activeProblemCandidate.reviewOpen)
      ? activeProblemCandidate
      : null;
  const activeProblemId = activeProblem?.id;
  const isReviewOpen = !!activeProblem?.reviewOpen;
  const attemptState = activeProblem?.attemptState || null;
  const attemptStarted = useMemo(() => {
    if (!attemptState) return true;
    if (attemptState === AttemptStates.ATTEMPT_ACTIVE) return true;
    if (
      [
        AttemptStates.COMMIT_OPEN,
        AttemptStates.REVIEW,
        AttemptStates.COMPLETED,
      ].includes(attemptState)
    ) {
      return true;
    }
    return isHintState(attemptState);
  }, [attemptState]);
  const getHintBaseSec = useCallback(
    () => focusElapsedSec,
    [focusElapsedSec]
  );

  const hintElapsedSec = useMemo(() => {
    return computeHintElapsedSec({
      hintStartFocusSec,
      tilDone: false,
      elapsedTime,
      focusElapsedSec,
    });
  }, [elapsedTime, focusElapsedSec, hintStartFocusSec]);

  const hintCountdowns = useMemo(() => {
    return computeHintCountdowns(hintUnlockAtSec, hintElapsedSec);
  }, [hintElapsedSec, hintUnlockAtSec]);

  const persistTimerState = useCallback(
    (state) => {
      timerStateRef.current = state;
      safeSave(timerStorageKey.current, state);
    },
    []
  );

  useEffect(() => {
    if (!isRunningProp) {
      if (isRunning) {
        setIsRunning(false);
        persistTimerState({
          ...timerStateRef.current,
          isRunning: false,
          endAt: null,
          lastTick: Date.now(),
        });
      }
      return;
    }

    if (isRunning) return;

    const now = Date.now();

    const baseState = timerStateRef.current;
    const durationSeconds = baseState.isPrimary
      ? primaryDuration
      : secondaryDuration;
    const nextState = {
      ...baseState,
      endAt: now + durationSeconds * 1000,
      isRunning: true,
      lastTick: now,
    };
    persistTimerState(nextState);
    timeLeftRef.current = durationSeconds;
    setTimeLeft(durationSeconds);
    setIsRunning(true);
  }, [
    isRunning,
    isRunningProp,
    persistTimerState,
    primaryDuration,
    secondaryDuration,
    tilDone,
  ]);

  const handleTaskCompletion = useCallback(async () => {
    setIsRunning(false);
    const {
      primaryDuration: focusSeconds,
      secondaryDuration: breakSeconds,
      numCycles: totalCycles,
    } = durationsRef.current;
    const updatedTask = {
      id,
      index,
      task: taskLabel,
      primaryDuration: focusSeconds,
      secondaryDuration: breakSeconds,
      numCycles: totalCycles,
      tilDone,
      isRunning: false,
      isProblemSolving: problemSolving,
    };
    try {
      await handleUpdate(updatedTask);
    } catch (error) {
      console.error("Failed to persist completed task state", error);
    }
    await Promise.resolve(onPause?.()).catch((error) =>
      console.error("Failed to pause task on completion", error)
    );
    await Promise.resolve(onToggle?.(id, true)).catch((error) =>
      console.error("Failed to mark task complete", error)
    );
    await Promise.resolve(onComplete?.(id)).catch((error) =>
      console.error("Failed to queue next task after completion", error)
    );
    persistTimerState({
      isPrimary: true,
      currentCycle: timerStateRef.current.currentCycle,
      endAt: null,
      isRunning: false,
      elapsedTilDone: timerStateRef.current.elapsedTilDone || 0,
      focusElapsedSec: timerStateRef.current.focusElapsedSec || 0,
      lastTick: Date.now(),
    });
    setTimeLeft(primaryDuration);
    timeLeftRef.current = primaryDuration;
    setIsPrimary(true);
  }, [
    handleUpdate,
    id,
    index,
    onComplete,
    onPause,
    onToggle,
    persistTimerState,
    primaryDuration,
    taskLabel,
    tilDone,
    problemSolving,
  ]);

  const computeRemaining = useCallback(
    (state) => {
      if (!state.endAt) {
        return state.isPrimary ? Math.round(primaryDuration) : Math.round(secondaryDuration);
      }
      return Math.max(
        0,
        Math.round((state.endAt - Date.now()) / 1000)
      );
    },
    [primaryDuration, secondaryDuration]
  );

  // Ticking loop driven by wall clock
  useEffect(() => {
    if (!isRunning) return;

    const tick = () => {
      let state = timerStateRef.current;
      if (!state.isRunning) return;
      const now = Date.now();
      const deltaSec = Math.max(0, Math.floor((now - state.lastTick) / 1000));
      if (deltaSec > 0) {
        const nextFocusElapsed = state.isPrimary
          ? (state.focusElapsedSec || 0) + deltaSec
          : state.focusElapsedSec || 0;
        const nextLastTick = state.lastTick + deltaSec * 1000;
        const nextState = {
          ...state,
          focusElapsedSec: nextFocusElapsed,
          lastTick: nextLastTick,
        };
        timerStateRef.current = nextState;
        state = nextState;
        setFocusElapsedSec(nextFocusElapsed);
      }
      const remaining = computeRemaining(state);
      if (remaining > 0) {
        setTimeLeft(remaining);
        timeLeftRef.current = remaining;
        return;
      }

      // Phase ended
      playBell();
      if (state.isPrimary) {
        // Move to break (even on the final cycle)
        const nextState = {
          ...state,
          isPrimary: false,
          endAt: now + secondaryDuration * 1000,
          lastTick: now,
        };
        persistTimerState(nextState);
        setIsPrimary(false);
        setTimeLeft(secondaryDuration);
        timeLeftRef.current = secondaryDuration;
      } else {
        if (state.currentCycle >= Math.max(1, numCycles) - 1) {
          handleTaskCompletion();
          return;
        }
        // Move back to focus, next cycle
        const nextCycle = state.currentCycle + 1;
        const nextState = {
          ...state,
          isPrimary: true,
          currentCycle: nextCycle,
          endAt: now + primaryDuration * 1000,
          lastTick: now,
        };
        persistTimerState(nextState);
        setIsPrimary(true);
        setCurrentCycle(nextCycle);
        setTimeLeft(primaryDuration);
        timeLeftRef.current = primaryDuration;
      }
    };

    tick();
    const interval = setInterval(tick, 500);
    const onVisibility = () => tick();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [
    computeRemaining,
    handleTaskCompletion,
    isRunning,
    numCycles,
    playBell,
    persistTimerState,
    primaryDuration,
    secondaryDuration,
    tilDone,
  ]);

  const toggleEdit = () => {
    setIsEditing((prev) => !prev);
  };

  const persistTask = (overrides = {}) => {
    handleUpdate({
      id,
      index,
      task: taskLabel,
      complete,
      primaryDuration,
      secondaryDuration,
      numCycles,
      tilDone,
      isRunning,
      order,
      isProblemSolving: problemSolving,
      ...overrides,
    });
  };

  const startStepper = (operation, setter) => {
    const run = () => {
      setter((prev) => operation(prev));
      stepperTimeout.current = window.setTimeout(run, 220);
    };
    run();
  };

  const stopStepper = () => {
    window.clearTimeout(stepperTimeout.current);
  };

  const handleToggleComplete = async () => {
    const nextComplete = !complete;

    if (isRunning) {
      await Promise.resolve(onPause?.()).catch((error) =>
        console.error("Failed to pause before toggling completion", error)
      );
      setIsRunning(false);
    }

    const updatedTask = {
      id,
      index,
      task: taskLabel,
      complete: nextComplete,
      primaryDuration,
      secondaryDuration,
      numCycles,
      tilDone,
      isRunning: false,
      isProblemSolving: problemSolving,
    };
    try {
      await handleUpdate(updatedTask);
    } catch (error) {
      console.error("Failed to persist completion toggle", error);
    }
    await Promise.resolve(onToggle?.(id, nextComplete)).catch((error) =>
      console.error("Failed to toggle task completion", error)
    );
    if (nextComplete) {
      await Promise.resolve(onComplete?.(id)).catch((error) =>
        console.error("Failed to queue next task after toggle", error)
      );
    }
  };

  const canRun = !complete && typeof onStart === "function";

  const toggleTimer = async () => {
    if (!canRun && !isRunning) return;

    if (isRunning) {
      await Promise.resolve(onPause?.()).catch((error) =>
        console.error("Failed to pause task timer", error)
      );
      setIsRunning(false);
      persistTimerState({
        ...timerStateRef.current,
        isRunning: false,
        endAt: null,
        lastTick: Date.now(),
      });
      return;
    }

    if (!canRun) return;

    const now = Date.now();
    const endAt = now + (isPrimary ? primaryDuration : secondaryDuration) * 1000;
    const nextState = {
      ...timerStateRef.current,
      isPrimary,
      endAt,
      isRunning: true,
      lastTick: now,
    };
    persistTimerState(nextState);
    timeLeftRef.current = isPrimary ? primaryDuration : secondaryDuration;
    setTimeLeft(timeLeftRef.current);
    setIsRunning(true);
    try {
      await Promise.resolve(onStart?.());
    } catch (error) {
      console.error("Failed to start task timer", error);
      setIsRunning(false);
      persistTimerState({ ...nextState, isRunning: false, endAt: null });
    }
  };

  const resetTimer = async () => {
    if (isRunning) {
      await Promise.resolve(onPause?.()).catch((error) =>
        console.error("Failed to pause before reset", error)
      );
    }
    setIsRunning(false);
    setTimeLeft(primaryDuration);
    setIsPrimary(true);
    setElapsedTime(0);
    setFocusElapsedSec(0);
    setHintStartFocusSec(null);
    setCurrentCycle(0);
    persistTask({ isRunning: false });
    persistTimerState({
      isPrimary: true,
      currentCycle: 0,
      endAt: null,
      isRunning: false,
      elapsedTilDone: 0,
      focusElapsedSec: 0,
      lastTick: Date.now(),
    });
  };

  const handleAnalyzeFromText = async (taskId, text) => {
    if (!onAnalyzeAndCreateProblemFromText) return;
    if (!isRunning) {
      await toggleTimer();
    }
    await onAnalyzeAndCreateProblemFromText(taskId, text);
  };

  const handleAnalyzeFromImage = async (taskId, fileOrUrl) => {
    if (!onAnalyzeAndCreateProblemFromImage) return;
    if (!isRunning) {
      await toggleTimer();
    }
    await onAnalyzeAndCreateProblemFromImage(taskId, fileOrUrl);
  };

  const handleAttemptGateSubmit = useCallback(
    async (plan) => {
      const baseSec = getHintBaseSec();
      if (!activeProblemId || !submitAttemptPlan) {
        queuedPlanRef.current = { ...plan, baseSec };
        return { queued: true };
      }

      pendingHintStartSecRef.current = baseSec;
      setHintStartFocusSec(baseSec);

      try {
        await submitAttemptPlan(activeProblemId, plan);
      } catch (error) {
        console.error("Failed to submit attempt plan", error);
      }
      return { queued: false };
    },
    [activeProblemId, getHintBaseSec, submitAttemptPlan]
  );

  const handleHintCheckSubmit = useCallback(
    async (problemId, response) => {
      if (!recordHintCheckResponse || !problemId) return;
      try {
        await recordHintCheckResponse(problemId, response);
      } catch (error) {
        console.error("Failed to record hint progress update", error);
      }
    },
    [recordHintCheckResponse]
  );

  const handleDeleteClick = async () => {
    if (isRunning) {
      await Promise.resolve(onPause?.()).catch((error) =>
        console.error("Failed to pause before delete", error)
      );
    }
    await Promise.resolve(onDelete?.()).catch((error) =>
      console.error("Failed to delete task", error)
    );
  };

  const saveEdits = () => {
    setIsEditing(false);
    persistTask({
      primaryDuration,
      secondaryDuration,
      numCycles,
      task: taskLabel,
    });
  };

  const formattedTime = formatTime(timeLeft);
  const isInfinite = numCycles >= 999;
  const totalCycles = isInfinite ? `${currentCycle + 1}/∞` : `${currentCycle + 1}/${Math.max(1, numCycles)}`;
  const solvedProblems = (problems || []).filter(
    (entry) => entry.status === "solved"
  );
  const metaLabel = [
    `${Math.floor(primaryDuration / 60)}m focus`,
    `${Math.floor(secondaryDuration / 60)}m break`,
    isInfinite ? "∞ cycles" : `${numCycles} cycles`,
  ]
    .filter(Boolean)
    .join(" · ");
  const phaseClass = isRunning
    ? isPrimary
      ? " task-card--active"
      : " task-card--break"
    : "";
  const timerStateClass = isRunning
    ? isPrimary
      ? " timer-display--active"
      : " timer-display--break"
    : "";
  const showHintTimer =
    problemSolving && typeof hintStartFocusSec === "number" && !isReviewOpen;
  const isBreakMode = isRunning && !isPrimary;
  const loggedHintLevels = useMemo(
    () => new Set((activeProblem?.hintTimeline || []).map((entry) => entry.level)),
    [activeProblem?.hintTimeline]
  );
  const nextHintLevel = useMemo(() => {
    const pending = HINT_LEVELS.find((entry) => !loggedHintLevels.has(entry.level));
    return pending?.level ?? null;
  }, [loggedHintLevels]);
  const nextHintRemaining =
    nextHintLevel && typeof hintCountdowns?.[nextHintLevel] === "number"
      ? hintCountdowns[nextHintLevel]
      : null;
  const hintTimerLabel =
    nextHintLevel == null
      ? "00:00"
      : typeof nextHintRemaining === "number"
      ? formatTime(nextHintRemaining)
      : "--:--";

  useEffect(() => {
    if (!problemSolving || !activeProblemId || !attemptStarted || isReviewOpen) {
      lastEligibleHintLevelsRef.current = [];
      pendingEligibleHintLevelsRef.current = [];
      return;
    }
    const eligible = Array.isArray(unlockedHintLevels)
      ? unlockedHintLevels
      : [];
    const previous = lastEligibleHintLevelsRef.current || [];
    const newlyEligible = eligible.filter((level) => !previous.includes(level));

    if (newlyEligible.length > 0) {
      if (isRunning && isPrimary && !isBreakMode) {
        const [nextLevel] = newlyEligible.sort((a, b) => a - b);
        notifyHintEligible(nextLevel);
      } else {
        const pending = new Set(pendingEligibleHintLevelsRef.current || []);
        newlyEligible.forEach((level) => pending.add(level));
        pendingEligibleHintLevelsRef.current = Array.from(pending).sort((a, b) => a - b);
      }
    }

    lastEligibleHintLevelsRef.current = eligible;
  }, [
    activeProblemId,
    attemptStarted,
    isBreakMode,
    isPrimary,
    isRunning,
    notifyHintEligible,
    problemSolving,
    unlockedHintLevels,
    isReviewOpen,
  ]);

  useEffect(() => {
    if (!problemSolving || !activeProblemId || !attemptStarted || isReviewOpen) return;
    if (!isRunning || !isPrimary || isBreakMode) return;
    const pending = pendingEligibleHintLevelsRef.current || [];
    if (!pending.length) return;
    const nextLevel = pending[0];
    if (notifyHintEligible(nextLevel)) {
      pendingEligibleHintLevelsRef.current = pending.filter((level) => level !== nextLevel);
    }
  }, [
    activeProblemId,
    attemptStarted,
    isBreakMode,
    isPrimary,
    isRunning,
    notifyHintEligible,
    problemSolving,
    isReviewOpen,
  ]);

  useEffect(() => {
    if (!telemetryEnabled) {
      lastBreakLockedRef.current = null;
      return;
    }
    if (!problemSolving || !activeProblemId || isReviewOpen) {
      lastBreakLockedRef.current = null;
      return;
    }
    if (lastBreakLockedRef.current === isBreakMode) return;
    lastBreakLockedRef.current = isBreakMode;

    void logTelemetryEvent?.({
      type: "pomodoro_break_locked_changed",
      taskId: id,
      problemId: activeProblemId,
      attemptId: activeProblem?.attemptId || null,
      breakLocked: isBreakMode,
    });
  }, [
    activeProblem?.attemptId,
    activeProblemId,
    id,
    isBreakMode,
    logTelemetryEvent,
    problemSolving,
    telemetryEnabled,
    isReviewOpen,
  ]);

  useEffect(() => {
    if (!activeProblemId || isReviewOpen) {
      lastProblemIdRef.current = null;
      setHintStartFocusSec(null);
      return;
    }
    if (lastProblemIdRef.current !== activeProblemId) {
      lastProblemIdRef.current = activeProblemId;
      pendingHintStartSecRef.current = null;
      setUnlockedHintLevels([]);
      setHintStartFocusSec(null);
    }
  }, [activeProblemId, attemptStarted, isReviewOpen]);

  useEffect(() => {
    if (!activeProblemId || !attemptStarted || isReviewOpen) return;
    if (typeof hintStartFocusSec === "number") return;
    const startAt =
      typeof pendingHintStartSecRef.current === "number"
        ? pendingHintStartSecRef.current
        : getHintBaseSec();
    pendingHintStartSecRef.current = null;
    setHintStartFocusSec(startAt);
  }, [activeProblemId, attemptStarted, getHintBaseSec, hintStartFocusSec, isReviewOpen]);

  useEffect(() => {
    if (!problemSolving) {
      previousActiveProblemIdRef.current = null;
      setIsHistoryOpen(false);
      return;
    }

    const previousId = previousActiveProblemIdRef.current;
    previousActiveProblemIdRef.current = activeProblemId || null;

    const justFinishedProblem = !!previousId && !activeProblemId;
    if (justFinishedProblem && solvedProblems.length > 0) {
      setIsHistoryOpen(true);
      return;
    }

    const justStartedProblem = !previousId && !!activeProblemId;
    if (justStartedProblem) {
      setIsHistoryOpen(false);
    }
  }, [activeProblemId, problemSolving, solvedProblems.length]);

  useEffect(() => {
    if (!activeProblemId) return;
    const nextHints = Array.isArray(activeProblem?.hints)
      ? [...activeProblem.hints].sort((a, b) => (a.level || 0) - (b.level || 0))
      : [];
    setActiveProblemHints(nextHints);
  }, [activeProblemId, activeProblem?.hints]);

  useEffect(() => {
    if (!activeProblemId || !problemSolving || isReviewOpen) {
      setUnlockedHintLevels([]);
      return;
    }
    const available = computeEligibleHintLevels(hintUnlockAtSec, hintElapsedSec);
    setUnlockedHintLevels(available);
  }, [activeProblemId, hintElapsedSec, hintUnlockAtSec, problemSolving, isReviewOpen]);

  useEffect(() => {
    if (!activeProblemId || isReviewOpen) return;
    if (!queuedPlanRef.current) return;
    if (!submitAttemptPlan) return;

    const queued = queuedPlanRef.current;
    queuedPlanRef.current = null;
    const baseSec =
      typeof queued.baseSec === "number" ? queued.baseSec : getHintBaseSec();
    pendingHintStartSecRef.current = baseSec;
    setHintStartFocusSec(baseSec);

    const { baseSec: _baseSec, ...plan } = queued;
    Promise.resolve(submitAttemptPlan(activeProblemId, plan)).catch((error) => {
      console.error("Failed to submit queued attempt plan", error);
    });
  }, [activeProblemId, getHintBaseSec, submitAttemptPlan, isReviewOpen]);

  const endWarmup = useCallback((reason) => {
    setWarmupActive(false);
    setWarmupCards([]);
    setWarmupLoading(false);
    if (reason === "done" || reason === "timeout") {
      setWarmupExitCta(reason);
    } else {
      setWarmupExitCta(null);
    }
  }, []);

  const handleWarmupRate = useCallback(
    async (card, rating) => {
      if (!reviewFlashcard) return;
      try {
        await reviewFlashcard(card, rating);
      } catch (error) {
        console.error("Failed to review flashcard", error);
      }
    },
    [reviewFlashcard]
  );

  useEffect(() => {
    if (!isStudyFocused || !isRunning || !isPrimary || isBreakMode || isReviewOpen) {
      return;
    }
    if (!getDueFlashcards) return;

    const sessionKey = `${currentCycle}-focus`;
    if (warmupSessionRef.current === sessionKey) return;
    warmupSessionRef.current = sessionKey;

    let isActive = true;
    setWarmupLoading(true);
    Promise.resolve(getDueFlashcards({ limit: 5, nowMs: Date.now() }))
      .then((cards) => {
        if (!isActive) return;
        const dueCards = Array.isArray(cards) ? cards : [];
        if (dueCards.length >= 2) {
          setWarmupCards(dueCards.slice(0, 5));
          setWarmupActive(true);
        } else {
          setWarmupCards([]);
          setWarmupActive(false);
        }
      })
      .catch((error) => {
        if (!isActive) return;
        console.error("Failed to load due flashcards", error);
        setWarmupCards([]);
        setWarmupActive(false);
      })
      .finally(() => {
        if (isActive) setWarmupLoading(false);
      });

    return () => {
      isActive = false;
    };
  }, [
    currentCycle,
    getDueFlashcards,
    isBreakMode,
    isPrimary,
    isReviewOpen,
    isRunning,
    isStudyFocused,
  ]);

  useEffect(() => {
    if (!isStudyFocused || isBreakMode || isReviewOpen) {
      endWarmup();
    }
  }, [endWarmup, isBreakMode, isReviewOpen, isStudyFocused]);

  return (
    <div
      className={`task-card${phaseClass}${isEditing ? " task-card--editing" : ""}`}
      role="listitem"
    >
      <div className="task-card__primary">
        <label className="checkbox-field">
          <input
            type="checkbox"
            checked={complete}
            onChange={handleToggleComplete}
          />
          <span></span>
        </label>
        <div className="stack stack--dense" style={{ minWidth: "0" }}>
          <div className="task-card__title-row">
            {isEditing ? (
              <input
                type="text"
                value={taskLabel}
                onChange={(e) => setTaskLabel(e.target.value)}
                disabled={complete}
              />
            ) : (
              <p
                className={`task-card__title${
                  complete ? " task-card__title--complete" : ""
                }`}
              >
                {taskLabel}
              </p>
            )}
            {problemSolving && <span className="pill-ghost">Study</span>}
          </div>
          <p className="task-card__meta">{metaLabel}</p>
        </div>
      </div>

      <div className="task-card__controls">
        <div className="task-card__timer">
          <div
            className={`timer-display${timerStateClass}`}
          >
            {formattedTime}
          </div>
          {showHintTimer && (
            <div className="timer-display timer-display--hint">
              {hintTimerLabel}
            </div>
          )}
          {hintToast && (
            <div className="hint-toast" role="status" aria-live="polite">
              {hintToast}
            </div>
          )}
          <span className="pill">
            {`Cycle ${totalCycles}`}
          </span>
          {problemSolving && (
            <button
              type="button"
              className="btn-icon btn--ghost task-focus-toggle"
              onClick={() => {
                setIsEditing(false);
                setIsStudyFocused((prev) => !prev);
              }}
              aria-label={isStudyFocused ? "Collapse study task" : "Expand study task"}
              title={isStudyFocused ? "Collapse" : "Expand"}
            >
              {isStudyFocused ? "▴" : "▾"}
            </button>
          )}
        </div>

        <div className="task-card__actions">
          {canRun && (
            <button
              type="button"
              className="btn task-action task-action--start"
              onClick={toggleTimer}
            >
              {isRunning ? "Pause" : "Start"}
            </button>
          )}
          <button
            type="button"
            className="btn btn--ghost task-action task-action--reset"
            onClick={resetTimer}
          >
            Reset
          </button>
          <button
            type="button"
            className="btn btn--ghost task-action task-action--edit"
            onClick={isEditing ? saveEdits : toggleEdit}
          >
            {isEditing ? "Done" : "Edit"}
          </button>
          <button
            type="button"
            className="btn btn--danger task-action task-action--delete"
            onClick={handleDeleteClick}
          >
            Delete
          </button>
        </div>
      </div>

      {!isStudyFocused && isEditing && (
        <div className="task-card__editor task-card__full">
          <div className="task-form__row">
            <div className="task-form__field" style={{ maxWidth: "200px" }}>
              <label>Cycles</label>
              <div className="timer-stepper">
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper(
                      (prev) => Math.max(1, prev - 1),
                      setNumCycles
                    )
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper(
                      (prev) => Math.max(1, prev - 1),
                      setNumCycles
                    )
                  }
                  onTouchEnd={stopStepper}
                >
                  −
                </button>
                <input
                  className="timer-stepper__input"
                  type="number"
                  min={1}
                  value={numCycles}
                  onChange={(e) =>
                    setNumCycles(Math.max(1, parseInt(e.target.value || "1", 10)))
                  }
                />
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper((prev) => prev + 1, setNumCycles)
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper((prev) => prev + 1, setNumCycles)
                  }
                  onTouchEnd={stopStepper}
                >
                  +
                </button>
              </div>
            </div>

            <div className="task-form__field" style={{ maxWidth: "220px" }}>
              <label>Focus minutes</label>
              <div className="timer-stepper">
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper(
                      (value) => Math.max(value - 60, 0),
                      setPrimaryDuration
                    )
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper(
                      (value) => Math.max(value - 60, 0),
                      setPrimaryDuration
                    )
                  }
                  onTouchEnd={stopStepper}
                >
                  −
                </button>
                <input
                  className="timer-stepper__input"
                  type="number"
                  min={0}
                  value={Math.floor(primaryDuration / 60)}
                  onChange={(e) => {
                    const value = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                    if (!Number.isNaN(value)) {
                      setPrimaryDuration(Math.min(Math.max(value, 0), 90) * 60);
                    }
                  }}
                />
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper(
                      (value) => Math.min(value + 60, 90 * 60),
                      setPrimaryDuration
                    )
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper(
                      (value) => Math.min(value + 60, 90 * 60),
                      setPrimaryDuration
                    )
                  }
                  onTouchEnd={stopStepper}
                >
                  +
                </button>
              </div>
            </div>

            <div className="task-form__field" style={{ maxWidth: "220px" }}>
              <label>Break minutes</label>
              <div className="timer-stepper">
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper(
                      (value) => Math.max(value - 60, 0),
                      setSecondaryDuration
                    )
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper(
                      (value) => Math.max(value - 60, 0),
                      setSecondaryDuration
                    )
                  }
                  onTouchEnd={stopStepper}
                >
                  −
                </button>
                <input
                  className="timer-stepper__input"
                  type="number"
                  min={0}
                  value={Math.floor(secondaryDuration / 60)}
                  onChange={(e) => {
                    const value = e.target.value === "" ? 0 : parseInt(e.target.value, 10);
                    if (!Number.isNaN(value)) {
                      setSecondaryDuration(
                        Math.min(Math.max(value, 0), 90) * 60
                      );
                    }
                  }}
                />
                <button
                  type="button"
                  className="timer-stepper__button"
                  onMouseDown={() =>
                    startStepper(
                      (value) => Math.min(value + 60, 90 * 60),
                      setSecondaryDuration
                    )
                  }
                  onMouseUp={stopStepper}
                  onMouseLeave={stopStepper}
                  onTouchStart={() =>
                    startStepper(
                      (value) => Math.min(value + 60, 90 * 60),
                      setSecondaryDuration
                    )
                  }
                  onTouchEnd={stopStepper}
                >
                  +
                </button>
              </div>
            </div>
          </div>

          <div className="task-form__row">
            <label className="checkbox-field">
              <input
                type="checkbox"
                checked={problemSolving}
                onChange={() => setProblemSolving((prev) => !prev)}
              />
              <span></span>
            </label>
            <p className="mini-label" style={{ margin: 0 }}>
              Mark as calculus study task to unlock the study panel.
            </p>
          </div>
        </div>
      )}

      {problemSolving && isStudyFocused && typeof document !== "undefined"
        ? createPortal(
            <div className="study-focus-overlay" role="dialog" aria-modal="true">
              <div className="study-focus-panel">
                <div className={`study-focus-card task-card${phaseClass}`}>
                  <div className="study-focus-card__header">
                    <div className="stack stack--dense" style={{ gap: "6px" }}>
                      <div className="cluster" style={{ gap: "10px" }}>
                        <p style={{ margin: 0, fontWeight: 700 }}>{taskLabel}</p>
                        <span className="pill-ghost">Study</span>
                      </div>
                      <p className="task-card__meta" style={{ margin: 0 }}>
                        {metaLabel}
                      </p>
                    </div>

                    <div className="study-focus-card__controls">
                      <div className="task-card__timer">
                        <div className={`timer-display${timerStateClass}`}>
                          {formattedTime}
                        </div>
                        {showHintTimer && (
                          <div className="timer-display timer-display--hint">
                            {hintTimerLabel}
                          </div>
                        )}
                        {hintToast && (
                          <div className="hint-toast" role="status" aria-live="polite">
                            {hintToast}
                          </div>
                        )}
                        <span className="pill">
                          {`Cycle ${totalCycles}`}
                        </span>
                        <button
                          type="button"
                          className="btn-icon btn--ghost task-focus-toggle"
                          onClick={() => setIsStudyFocused(false)}
                          aria-label="Collapse study task"
                          title="Collapse"
                        >
                          ▴
                        </button>
                      </div>

                      <div className="task-card__actions">
                        {canRun && (
                          <button
                            type="button"
                            className="btn task-action task-action--start"
                            onClick={toggleTimer}
                          >
                            {isRunning ? "Pause" : "Start"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn--ghost task-action task-action--reset"
                          onClick={resetTimer}
                        >
                          Reset
                        </button>
                        <button
                          type="button"
                          className="btn btn--danger task-action task-action--delete"
                          onClick={handleDeleteClick}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </div>

                  {isBreakMode && (
                    <div className="study-focus-break-banner">
                      <p style={{ margin: 0, fontWeight: 700 }}>
                        Break mode · {formattedTime}
                      </p>
                      <p className="mini-label" style={{ margin: 0 }}>
                        Studying is paused during breaks.
                      </p>
                    </div>
                  )}

                  <div
                    className={`study-focus-body${
                      isBreakMode ? " study-focus-body--disabled" : ""
                    }`}
                    aria-disabled={isBreakMode}
                  >
                    <div className="task-card__full">
                      {warmupLoading && !warmupActive ? (
                        <div className="card-quiet">
                          <p style={{ margin: 0, fontWeight: 700 }}>
                            Loading warmup cards…
                          </p>
                          <p className="mini-label" style={{ margin: "6px 0 0" }}>
                            Checking what is due.
                          </p>
                        </div>
                      ) : warmupActive ? (
                        <FlashcardWarmup
                          cards={warmupCards}
                          onRate={handleWarmupRate}
                          onComplete={endWarmup}
                          maxDurationMs={3 * 60 * 1000}
                        />
                      ) : (
                        <>
                          {warmupExitCta ? (
                            <div className="card-quiet" style={{ marginBottom: "12px" }}>
                              <p style={{ margin: 0, fontWeight: 700 }}>
                                {warmupExitCta === "timeout"
                                  ? "Warmup finished (time cap reached)."
                                  : "Warmup finished."}
                              </p>
                              <p className="mini-label" style={{ margin: "6px 0 0" }}>
                                Want to keep reviewing or edit cards?
                              </p>
                              <div className="cluster" style={{ marginTop: "10px", gap: "10px" }}>
                                <button
                                  type="button"
                                  className="btn btn--primary btn--small"
                                  onClick={() => window.location.assign("/Flashcards?review=due")}
                                >
                                  Continue flashcards
                                </button>
                                <button
                                  type="button"
                                  className="btn btn--ghost btn--small"
                                  onClick={() => setWarmupExitCta(null)}
                                >
                                  Dismiss
                                </button>
                              </div>
                            </div>
                          ) : null}
                          <ProblemPanel
                            task={{ id, task: taskLabel, isProblemSolving: problemSolving }}
                            problem={activeProblem}
                            hints={activeProblemHints}
                            unlockedHintLevels={unlockedHintLevels}
                            isBreakMode={isBreakMode}
                            onStart={onStartProblem}
                            onFinish={onFinishProblem}
                            onCloseReview={onCloseReview}
                            onRecordHint={onRecordHint}
                            onRequestHint={onRequestHint}
                            onRequestDesmos={requestDesmosExpressions}
                            onUpdate={onUpdateProblem}
                            onReportLatexMismatch={onReportLatexMismatch}
                            onAnalyzeText={handleAnalyzeFromText}
                            onAnalyzeImage={handleAnalyzeFromImage}
                            onOcrLatexFromImage={onOcrLatexFromImage}
                            onCheckAnswerForProblem={onCheckAnswerForProblem}
                            onSubmitAttemptPlan={handleAttemptGateSubmit}
                            onSubmitHintCheck={handleHintCheckSubmit}
                            onAnswerChat={answerChat}
                            onCreateFlashcard={createFlashcard}
                            hintCountdowns={hintCountdowns}
                            variant={isReviewOpen ? "review" : "active"}
                          />
                        </>
                      )}
                    </div>

                    {solvedProblems.length > 0 && (
                      <div className="task-card__full">
                        <details
                          className="problem-panel__section"
                          open={isHistoryOpen}
                          onToggle={(event) => setIsHistoryOpen(event.currentTarget.open)}
                        >
                          <summary>{`Completed problems (${solvedProblems.length})`}</summary>
                          <div className="stack stack--dense" style={{ marginTop: "12px" }}>
                            {solvedProblems.map((entry) => {
                              const isExpanded = expandedSolved[entry.id];
                              return (
                                <div key={entry.id} className="card-quiet">
                                  <div
                                    className="page-section__headline"
                                    style={{ marginBottom: "8px" }}
                                  >
                                    <div className="stack stack--dense" style={{ gap: "4px" }}>
                                      <p style={{ margin: 0, fontWeight: 700 }}>
                                        {entry.problemType || "Solved problem"}
                                      </p>
                                      <p className="mini-label" style={{ margin: 0 }}>
                                        {entry.approach || "—"} ·{" "}
                                        {entry.timestamp
                                          ? new Date(entry.timestamp).toLocaleString()
                                          : "—"}
                                      </p>
                                    </div>
                                    <button
                                      type="button"
                                      className="btn btn--ghost"
                                      onClick={() =>
                                        setExpandedSolved((prev) => ({
                                          ...prev,
                                          [entry.id]: !prev[entry.id],
                                        }))
                                      }
                                    >
                                      {isExpanded ? "Collapse" : "Expand"}
                                    </button>
                                  </div>
                                  {isExpanded && (
                                    <div className="task-card__full">
                                      <ProblemPanel
                                        task={{
                                          id,
                                          task: taskLabel,
                                          isProblemSolving: true,
                                        }}
                                        problem={entry}
                                        hints={
                                          Array.isArray(entry?.hints)
                                            ? [...entry.hints].sort(
                                                (a, b) => (a.level || 0) - (b.level || 0)
                                              )
                                            : []
                                        }
                                        unlockedHintLevels={HINT_LEVELS.map(
                                          (levelEntry) => levelEntry.level
                                        )}
                                        onUpdate={onUpdateProblem}
                                        onRequestDesmos={requestDesmosExpressions}
                                        onOcrLatexFromImage={onOcrLatexFromImage}
                                        onCheckAnswerForProblem={onCheckAnswerForProblem}
                                        onSubmitHintCheck={handleHintCheckSubmit}
                                        variant="review"
                                      />
                                    </div>
                                  )}
                                </div>
                              );
                            })}
                          </div>
                        </details>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>,
            document.body
          )
        : null}

    </div>
  );
};

export default ToDoItem;
